import { useTranslation } from 'react-i18next';

import { CONSIDERATION_STATUSES, type ConsiderationStatus } from '@/api/vocabularies';
import { Checkbox } from '@/components/ui/Checkbox';

interface Props {
  /**
   * The statuses actually being sent — `appliedStatus`, not what the URL literally says. Under
   * §6.3's default that is `NEW`, `INTERVIEWING`, `OFFER`, and the boxes show them ticked because
   * those three *are* the filter; a control that showed nothing ticked while three statuses were
   * being filtered on is the "looks unfiltered" failure inside the control itself.
   */
  applied: ConsiderationStatus[];
  onChange: (statuses: ConsiderationStatus[]) => void;
}

/**
 * §6.3's status filter: **multi-select, and the five values come from the schema**
 * (`CONSIDERATION_STATUSES`, derived from `components.schemas.ConsiderationStatus` — TASK-061), so a
 * status added on the backend appears here without an edit and a status typed out by hand fails
 * `api.test.ts`'s guard.
 *
 * ⚠️ **Five checkboxes rather than a multi-select `Select`, deliberately.** §4's inventory has no
 * multi-select control and Radix's `Select` is single-value; the alternatives were a new primitive
 * (which this task is told not to add) or a popover holding checkboxes, which hides the applied set
 * behind a click on the one screen whose named failure is a filter you cannot see. A `fieldset` with
 * five boxes is the applied set, visible, keyboard-operable, and five elements wide at most.
 *
 * ⚠️ **Unticking the last box returns to the default rather than selecting nothing.** An empty
 * selection is not expressible in the address — an absent `status` is precisely what §6.3's default
 * keys on (`candidateFilters.ts`) — and it would ask the API for the rows whose status is none of
 * the five, which is no rows at all. So the control cannot express it, the chip says which of the two
 * is in force, and *Show all statuses* is how everything is asked for.
 */
export function StatusFilter({ applied, onChange }: Props) {
  const { t } = useTranslation(['candidates', 'components']);

  const toggle = (status: ConsiderationStatus, checked: boolean) =>
    onChange(
      checked
        ? // In the schema's order, so `?status=` reads the same however the boxes were clicked.
          CONSIDERATION_STATUSES.filter((one) => one === status || applied.includes(one))
        : applied.filter((one) => one !== status),
    );

  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="text-xs font-medium text-ink-700">{t('candidates:filters.status')}</legend>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2">
        {CONSIDERATION_STATUSES.map((status) => (
          <Checkbox
            key={status}
            id={`candidate-status-${status}`}
            // The same word `StatusDot` renders in the Status column (§3: colour never alone).
            label={t(`components:status.${status}`)}
            checked={applied.includes(status)}
            onCheckedChange={(checked) => toggle(status, checked === true)}
          />
        ))}
      </div>
    </fieldset>
  );
}
